import { AppConfigService } from 'src/app/appConfig.service';

/**
 * The website's support chat inside the app (docs/support-chat-in-app.md in Synced-Backend),
 * in place of Intercom when the backend's frontend-config says supportChat: "synced".
 *
 * It loads the website's script (supportChatScriptUrl) once and mounts its <synced-support-chat>
 * element for the signed-in user. The chat talks to the backend's api/support/me with the app's
 * own sign-in cookie, so the backend knows who is writing; the name and email passed here only
 * greet them, and the selected organisation tells the team which account they are writing from. ManageIntercom delegates to this, so the sidebar's Support item, the error toasts'
 * support link and every page's identify call work unchanged:
 *
 * - show / hide / toggle open and close the panel;
 * - when the panel closes it raises "intercom:closed", as Intercom's onHide did, so the sidebar
 *   un-highlights Support;
 * - the notifications hub's "SupportReply" calls notifyReply(), so a team reply shows at once;
 * - a ?support_handoff= code from the website (captureHandoff) is handed to the chat, which claims
 *   the conversation the visitor started there.
 */
export class SupportChat {
  static readonly TAG = 'synced-support-chat';
  static readonly HANDOFF_PARAM = 'support_handoff';
  private static readonly HANDOFF_KEY = 'SupportHandoffCode';

  private static provider: 'synced' | 'intercom' = 'intercom';
  private static scriptUrl: string | undefined;
  private static apiUrl: string | undefined;
  private static scriptRequested = false;
  private static el: any = null;
  private static wantOpen = false;
  private static mounting = false;

  /** Reads the switch. Call once the config has loaded; harmless to call again. */
  static configure(config: AppConfigService): void {
    try {
      this.provider = config.supportChat;
      this.scriptUrl = config.supportChatScriptUrl;
      this.apiUrl = config.apiURL;
    } catch {
      this.provider = 'intercom';
    }
  }

  static get isSynced(): boolean {
    return this.provider === 'synced';
  }

  /**
   * A website visitor following Sign in or Create account arrives with ?support_handoff=: keep the
   * code for this tab until the chat mounts and claims it. Call before the router rewrites the URL.
   */
  static captureHandoff(): void {
    try {
      const params = new URLSearchParams(window.location.search);
      const code = params.get(this.HANDOFF_PARAM);
      if (!code) return;
      if (/^[A-Za-z0-9_-]{43}$/.test(code)) sessionStorage.setItem(this.HANDOFF_KEY, code);
      // Out of the address bar at once: a copied link, a screenshot or the history must not carry
      // a code that claims someone's conversation for whoever signs in with it first.
      params.delete(this.HANDOFF_PARAM);
      const query = params.toString();
      window.history.replaceState(window.history.state, '',
        window.location.pathname + (query ? `?${query}` : '') + window.location.hash);
    } catch {
      /* storage unavailable: the conversation simply stays on the website */
    }
  }

  /** Mounts the chat for the signed-in user (again, if a different user is now signed in). */
  static mount(): void {
    if (!this.isSynced || !this.scriptUrl) return;
    const userId = localStorage.getItem('Id');
    if (!userId) return;
    if (this.el && this.el.getAttribute('user-id') === userId) return;
    this.unmount();
    this.loadScript();
    if (this.mounting) return;
    this.mounting = true;
    // Created only once the element is defined, so the properties set here are the element's own.
    customElements.whenDefined(this.TAG).then(() => {
      this.mounting = false;
      if (this.el || localStorage.getItem('Id') !== userId) return;
      const el: any = document.createElement(this.TAG);
      el.setAttribute('api-base', this.apiUrl || '');
      el.setAttribute('user-id', userId);
      el.setAttribute('user-name', localStorage.getItem('UserName') || '');
      el.setAttribute('user-email', localStorage.getItem('Email') || '');
      const handoff = sessionStorage.getItem(this.HANDOFF_KEY);
      if (handoff) {
        el.setAttribute('handoff-code', handoff);
        sessionStorage.removeItem(this.HANDOFF_KEY);
      }
      el.getToken = () => localStorage.getItem('AccessToken');
      // Read on every send, so a switch of organisation is followed. The backend keeps it only when
      // the user belongs to it: it tells the team which account the user is writing from.
      el.getOrganisationId = () => localStorage.getItem('SelectedOrganization');
      el.addEventListener('synced-support:open-change', (e: CustomEvent) => {
        if (!e.detail?.open) window.dispatchEvent(new CustomEvent('intercom:closed'));
      });
      el.addEventListener('synced-support:attention', (e: CustomEvent) => {
        window.dispatchEvent(new CustomEvent('support-chat:attention', { detail: !!e.detail?.attention }));
      });
      document.body.appendChild(el);
      this.el = el;
      if (this.wantOpen) el.open();
      this.wantOpen = false;
    });
  }

  static unmount(): void {
    this.el?.remove();
    this.el = null;
    this.wantOpen = false;
  }

  static show(): void {
    if (this.el) this.el.open();
    else this.wantOpen = true;
  }

  static hide(): void {
    this.el?.close();
    this.wantOpen = false;
  }

  static notifyReply(): void {
    this.el?.notifyReply?.();
  }

  private static loadScript(): void {
    if (this.scriptRequested || customElements.get(this.TAG)) return;
    this.scriptRequested = true;
    const s = document.createElement('script');
    s.src = this.scriptUrl!;
    s.async = true;
    s.onerror = () => {
      // Unreachable script: Support does nothing rather than throwing; try again on the next mount.
      this.scriptRequested = false;
      this.mounting = false;
      console.error('The support chat could not be loaded from', this.scriptUrl);
    };
    document.head.appendChild(s);
  }
}
